import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  createProperty,
  deletePropertyApi,
  fetchProperties,
  patchPropertyApi,
  type AuthProperty,
} from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import ConfirmDialog from '@/components/ConfirmDialog';

export default function PropertiesTab() {
  const { role, refresh } = useAuth();
  const isAdmin = role === 'admin';
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['settings', 'properties'],
    queryFn: fetchProperties,
  });

  const create = useMutation({
    mutationFn: (body: { name: string; description: string | null; address: string | null }) =>
      createProperty(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings', 'properties'] });
      await refresh();
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      patchPropertyApi(id, { name }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings', 'properties'] });
      await refresh();
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => deletePropertyApi(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings', 'properties'] });
      await refresh();
    },
  });

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    // Snapshot the form values up front — `event.currentTarget` is
    // nulled out by React once the synchronous handler returns, so
    // anything we need from the form must be read before the await.
    const fd = new FormData(event.currentTarget);
    try {
      await create.mutateAsync({
        name: String(fd.get('name') ?? '').trim(),
        description: String(fd.get('description') ?? '').trim() || null,
        address: String(fd.get('address') ?? '').trim() || null,
      });
      // Closing the create card unmounts the form; the inputs reset
      // by virtue of getting a fresh mount next time the user opens
      // the affordance. No explicit form.reset() needed.
      setCreating(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create property');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {list.data?.length ?? 0} {list.data?.length === 1 ? 'property' : 'properties'} in this account.
        </p>
        {isAdmin ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setCreating((c) => !c)}
            data-testid="settings-add-property"
          >
            <Plus className="h-4 w-4 mr-1" aria-hidden /> Add property
          </Button>
        ) : null}
      </div>

      {creating && isAdmin ? (
        <Card>
          <CardContent className="pt-5">
            <form onSubmit={onSubmit} className="space-y-3" data-testid="property-create-form">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="font-mono text-[11px] uppercase tracking-wider">
                  Name
                </Label>
                <Input id="name" name="name" required className="h-11" data-testid="property-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="address" className="font-mono text-[11px] uppercase tracking-wider">
                  Address (optional)
                </Label>
                <Input id="address" name="address" className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="description" className="font-mono text-[11px] uppercase tracking-wider">
                  Description (optional)
                </Label>
                <Input id="description" name="description" className="h-11" />
              </div>
              {error ? (
                <p className="text-sm text-warning font-medium" role="alert">
                  {error}
                </p>
              ) : null}
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? 'Creating…' : 'Create property'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {deleteError ? (
        <div
          role="alert"
          className="rounded-md border border-warning/40 bg-warning/5 px-4 py-3"
          data-testid="property-delete-error"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-warning mb-1">
            Couldn't delete
          </p>
          <p className="text-sm text-foreground">{deleteError}</p>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            className="mt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="properties-list">
        {(list.data ?? []).map((p) => (
          <PropertyRow
            key={p.id}
            property={p}
            isAdmin={isAdmin}
            isPending={patch.isPending || del.isPending}
            onRename={(name) => patch.mutateAsync({ id: p.id, name })}
            onDeleteRequest={() => setConfirmDeleteId(p.id)}
          />
        ))}
      </ul>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteId(null);
        }}
        title="Delete this property?"
        description="The property and every photo, run, log, equipment, tool, and project under it will be removed. This can't be undone."
        confirmLabel="Delete property"
        busyLabel="Deleting…"
        tone="destructive"
        testId="confirm-delete-property"
        onConfirm={async () => {
          if (!confirmDeleteId) return;
          try {
            await del.mutateAsync(confirmDeleteId);
            setDeleteError(null);
            setConfirmDeleteId(null);
          } catch (err) {
            setDeleteError(
              err instanceof Error ? err.message : 'Could not delete property',
            );
            setConfirmDeleteId(null);
          }
        }}
      />
    </div>
  );
}

function PropertyRow({
  property,
  isAdmin,
  isPending,
  onRename,
  onDeleteRequest,
}: {
  property: AuthProperty;
  isAdmin: boolean;
  isPending: boolean;
  onRename: (name: string) => Promise<unknown>;
  onDeleteRequest: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(property.name);
  return (
    <li className="flex items-start justify-between gap-3 px-4 py-3" data-testid={`property-row-${property.slug}`}>
      <div className="min-w-0 flex-1">
        {editing ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              await onRename(name);
              setEditing(false);
            }}
            className="flex items-center gap-2"
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" autoFocus />
            <Button type="submit" size="sm" disabled={isPending}>
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setName(property.name);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </form>
        ) : (
          <>
            <p className="font-display text-base">{property.name}</p>
            <p className="text-xs text-muted-foreground font-mono">
              {property.slug} · {property.status}
            </p>
            {property.address ? (
              <p className="text-sm text-muted-foreground">{property.address}</p>
            ) : null}
          </>
        )}
      </div>
      {isAdmin && !editing ? (
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setEditing(true)}
            data-testid={`rename-${property.slug}`}
          >
            Rename
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onDeleteRequest}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            data-testid={`delete-${property.slug}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ) : null}
    </li>
  );
}
